import { IconButton, Stack, styled, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { ChevronDown, ChevronUp } from 'mdi-material-ui';
import { useChartsTheme } from '@/components';
import { StatusHistoryLabelGrouping } from '@/core';
import { OptionsEditorGroup } from '@/plugin-editor-components';
import { ThresholdColorPicker } from '@/plugin-editor-components/components/ThresholdsEditor/ThresholdColorPicker';

interface LabelColorMappingsEditorProps {
  colorByLabel: string;
  labelGrouping: StatusHistoryLabelGrouping;
  onChange: (updated: StatusHistoryLabelGrouping) => void;
}

const MappingRow = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  padding: `${theme.spacing(0.5)} 0`,
}));

export function LabelColorMappingsEditor({ colorByLabel, labelGrouping, onChange }: LabelColorMappingsEditorProps) {
  const chartsTheme = useChartsTheme();
  const { defaultColor } = chartsTheme.thresholds;
  const mappings = labelGrouping.color_mapping?.mappings ?? [];

  const handleColorChange = (labelValue: string, newColor: string) => {
    onChange(
      produce(labelGrouping, (draft) => {
        const mapping = draft.color_mapping?.mappings?.find((m) => m.label_value === labelValue);
        if (mapping) mapping.color = newColor;
      })
    );
  };

  const handleMove = (idx: number, direction: -1 | 1) => {
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= mappings.length) return;
    onChange(
      produce(labelGrouping, (draft) => {
        const arr = draft.color_mapping?.mappings;
        if (!arr) return;
        const [moved] = arr.splice(idx, 1);
        if (moved) arr.splice(targetIdx, 0, moved);
      })
    );
  };

  const handleDefaultColorChange = (newColor: string) => {
    onChange(
      produce(labelGrouping, (draft) => {
        if (!draft.color_mapping) draft.color_mapping = {};
        draft.color_mapping.base = { color: newColor };
      })
    );
  };

  return (
    <OptionsEditorGroup title="Label Color Mappings">
      <Typography variant="caption" color="text.secondary">
        Order determines priority when states overlap.
      </Typography>
      {mappings.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No values found for label &quot;{colorByLabel}&quot;. Run the query to discover values.
        </Typography>
      )}
      {mappings.map((mapping, idx) => (
        <MappingRow key={mapping.label_value} direction="row" spacing={1}>
          <Stack direction="column" sx={{ gap: 0 }}>
            <IconButton
              size="small"
              disabled={idx === 0}
              onClick={() => handleMove(idx, -1)}
              aria-label={`Move ${mapping.label_value} up`}
              sx={{ padding: 0 }}
            >
              <ChevronUp fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={idx === mappings.length - 1}
              onClick={() => handleMove(idx, 1)}
              aria-label={`Move ${mapping.label_value} down`}
              sx={{ padding: 0 }}
            >
              <ChevronDown fontSize="small" />
            </IconButton>
          </Stack>
          <ThresholdColorPicker
            color={mapping.color ?? defaultColor}
            onColorChange={(newColor) => handleColorChange(mapping.label_value, newColor)}
            label={mapping.label_value}
          />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {mapping.label_value}
          </Typography>
        </MappingRow>
      ))}
      <MappingRow direction="row" spacing={1}>
        <ThresholdColorPicker
          color={labelGrouping.color_mapping?.base?.color ?? defaultColor}
          onColorChange={handleDefaultColorChange}
          label="default"
        />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Default (unmatched values)
        </Typography>
      </MappingRow>
    </OptionsEditorGroup>
  );
}
